"use client";

import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown, ArrowUp, ChevronDown, Minus } from "lucide-react";
import { useState } from "react";

interface LeveragePoint {
  point: string;
  strength: number;
  talkingPoints: string[];
  supportingEvidence: string;
  trend: "positive" | "negative" | "neutral";
  metric?: {
    label: string;
    value: string;
    change?: string;
  };
}

interface LeverageDashboardProps {
  leveragePoints: LeveragePoint[];
}

function StrengthIndicator({
  strength,
  trend,
  size = "default",
}: {
  strength: number;
  trend: "positive" | "negative" | "neutral";
  size?: "default" | "small";
}) {
  const bars = [1, 2, 3];
  const filledBars = Math.ceil((strength / 100) * 3);

  return (
    <div className="flex items-center gap-1">
      <div className="flex gap-0.5">
        {bars.map((bar) => (
          <div
            key={bar}
            className={cn(
              "rounded-full transition-all",
              size === "small" ? "h-3 w-0.5" : "h-4 w-0.5",
              bar <= filledBars
                ? trend === "positive"
                  ? "bg-emerald-500"
                  : trend === "negative"
                    ? "bg-rose-500"
                    : "bg-amber-500"
                : "bg-gray-200",
            )}
          />
        ))}
      </div>
    </div>
  );
}

function getTrendIcon(trend: "positive" | "negative" | "neutral") {
  const iconClass = "h-4 w-4";

  if (trend === "positive")
    return <ArrowUp className={cn(iconClass, "text-emerald-600")} />;
  if (trend === "negative")
    return <ArrowDown className={cn(iconClass, "text-rose-600")} />;
  return <Minus className={cn(iconClass, "text-amber-600")} />;
}

export function LeverageDashboard({ leveragePoints }: LeverageDashboardProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const getLabelByStrength = (
    strength: number,
    trend: "positive" | "negative" | "neutral",
  ) => {
    if (trend === "positive") {
      if (strength >= 80) return "Strong leverage";
      if (strength >= 60) return "Good leverage";
      if (strength >= 40) return "Moderate leverage";
      return "Minimal leverage";
    } else if (trend === "negative") {
      if (strength >= 80) return "Major concern";
      if (strength >= 60) return "Significant concern";
      if (strength >= 40) return "Moderate concern";
      return "Minor concern";
    } else {
      return "Neutral factor";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold">Negotiation Leverage</h3>
          <p className="text-sm text-muted-foreground">
            {leveragePoints.length} points to consider in your offer
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {leveragePoints.map((point, index) => {
          const cleanTitle = point.point.replace(/\s*\([^)]*\)/g, "").trim();
          const isExpanded = expandedIndex === index;

          return (
            <motion.div
              key={`leverage-${point.point}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={cn(
                "group relative overflow-hidden rounded-lg border border-border bg-card transition-all",
                isExpanded ? "shadow-sm" : "hover:bg-accent/50",
              )}
            >
              <button
                type="button"
                onClick={() => setExpandedIndex(isExpanded ? null : index)}
                className="relative w-full text-left"
              >
                {/* Card Header */}
                <div className="flex items-start gap-4 p-4 sm:p-4">
                  {/* Trend and Strength Indicator */}
                  <div
                    className={cn(
                      "flex flex-col items-center gap-2 pt-1",
                      point.trend === "positive"
                        ? "text-emerald-600"
                        : point.trend === "negative"
                          ? "text-rose-600"
                          : "text-amber-600",
                    )}
                  >
                    {getTrendIcon(point.trend)}
                    <StrengthIndicator
                      strength={point.strength}
                      trend={point.trend}
                      size="small"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="pr-6">
                      <h4 className="font-medium text-gray-900">
                        {cleanTitle}
                      </h4>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span
                          className={cn(
                            "text-xs font-medium",
                            point.trend === "positive"
                              ? "text-emerald-600"
                              : point.trend === "negative"
                                ? "text-rose-600"
                                : "text-amber-600",
                          )}
                        >
                          {getLabelByStrength(point.strength, point.trend)}
                        </span>
                        {point.metric && (
                          <>
                            <span className="text-xs text-gray-300">•</span>
                            <span className="text-xs text-muted-foreground">
                              Impact: {point.metric.value}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expand Button */}
                  <div className="absolute right-3 top-4">
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 text-muted-foreground transition-transform duration-200",
                        isExpanded && "rotate-180",
                      )}
                    />
                  </div>
                </div>
              </button>

              {/* Expanded Content */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="border-t border-gray-100">
                      {/* Evidence Section */}
                      <div className="bg-gray-50/50 px-4 py-3">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {point.supportingEvidence}
                        </p>
                      </div>

                      {/* Talking Points */}
                      <div className="px-4 py-3">
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          TALKING POINTS
                        </p>
                        <ul className="space-y-2">
                          {point.talkingPoints.map((talkingPoint, i) => (
                            <motion.li
                              key={`${point.point}-talking-point-${i}`}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.1 }}
                              className="flex items-start gap-3 text-sm"
                            >
                              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-300" />
                              <span className="text-muted-foreground leading-relaxed">
                                {talkingPoint}
                              </span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
